import React from 'react';
import { Box, Container, Grid, Stack, Typography, Link } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ToolsHeader from '../common/ToolsHeader';
import LeagueCard, { LeagueCardProps, LeagueTool } from './LeagueCard';
import AllAccessUpsellBanner from './AllAccessUpsellBanner';

// Slate info types - matches API response
export interface SlateInfo {
  site: string;
  sport: string;
  type: string;
  startTime: string;
  name: string;
  slateId: number;
  matchupInfo?: Array<{
    homeTeamAbbrev: string;
    awayTeamAbbrev: string;
    gameTime: string;
  }>;
}

export interface LeagueSlateInfo {
  sport: string;
  nextSlate: SlateInfo | null;
  isLive: boolean;
  gameCount: number;
}

// League configuration
export interface LeagueConfig {
  id: string;
  name: string;
  icon: string;
  tools: LeagueTool[];
}

// Default tools configuration for each league type
const getToolsForLeague = (league: string): LeagueTool[] => {
  const leagueLower = league.toLowerCase();
  const baseTools: LeagueTool[] = [
    {
      name: 'Contest Simulations',
      description: 'Generate lineups, simulate contests, and evaluate ROI',
      path: `/contestgenerator/${league}`,
    },
    {
      name: 'Single Lineup Simulator',
      description: 'Build and test lineups one at a time',
      path: `/singlelineupsim/${league}`,
    },
    {
      name: 'Data Hub',
      description: 'Projections and player data tables',
      path: `/datahub/${league}`,
    },
  ];

  // Add Late Swap for supported leagues
  const lateSwapLeagues = ['nfl', 'nba', 'mlb', 'cfb'];
  if (lateSwapLeagues.includes(leagueLower)) {
    baseTools.push({
      name: 'Late Swap',
      description: 'Swap lineups quickly when news breaks',
      path: `/lateswap/${league}`,
    });
  }

  return baseTools;
};

// All leagues configuration
const ALL_LEAGUES: LeagueConfig[] = [
  {
    id: 'NFL',
    name: 'NFL',
    icon: `${process.env.PUBLIC_URL}/images/NFL.png`,
    tools: [
      ...getToolsForLeague('NFL'),
      { name: 'Props', description: 'Find +EV player props with projections', path: '/props/NFL' },
    ],
  },
  {
    id: 'NBA',
    name: 'NBA',
    icon: `${process.env.PUBLIC_URL}/images/NBA.png`,
    tools: [
      ...getToolsForLeague('NBA'),
      {
        name: 'Props',
        description: 'Find +EV player props with projections',
        path: '/props/NBA',
      },
    ],
  },
  {
    id: 'NHL',
    name: 'NHL',
    icon: 'https://awesemostorage.blob.core.windows.net/sports/nhl/nhl.png',
    tools: [
      {
        name: 'Contest Simulations',
        description: 'Generate lineups, simulate contests, and evaluate ROI',
        path: '/contestgenerator/NHL',
      },
      {
        name: 'Single Lineup Simulator',
        description: 'Build and test lineups one at a time',
        path: '/singlelineupsim/NHL',
      },
      {
        name: 'Data Hub',
        description: 'Projections and player data tables',
        path: '/datahub/NHL',
      },
      {
        name: 'Props',
        description: 'Find +EV player props with projections',
        path: '/props/NHL',
      },
    ],
  },
  {
    id: 'MMA',
    name: 'MMA',
    icon: `${process.env.PUBLIC_URL}/images/MMA.png`,
    tools: getToolsForLeague('MMA').filter(t => t.name !== 'Late Swap'),
  },
  {
    id: 'NASCAR',
    name: 'NASCAR',
    icon: `${process.env.PUBLIC_URL}/images/NASCAR.png`,
    tools: getToolsForLeague('NASCAR').filter(t => t.name !== 'Late Swap'),
  },
  {
    id: 'CFB',
    name: 'CFB',
    icon: `${process.env.PUBLIC_URL}/images/CFB.png`,
    tools: getToolsForLeague('CFB'),
  },
  {
    id: 'MLB',
    name: 'MLB',
    icon: `${process.env.PUBLIC_URL}/images/MLB.png`,
    tools: getToolsForLeague('MLB'),
  },
  {
    id: 'PGA',
    name: 'PGA',
    icon: `${process.env.PUBLIC_URL}/images/PGA.png`,
    tools: getToolsForLeague('PGA').filter(t => t.name !== 'Late Swap'),
  },
];

interface ToolsHomeProps {
  subscribedLeagues: string[];
  isAllAccess?: boolean;
  leagueSlateInfo?: Record<string, LeagueSlateInfo>;
}

// Helper function to format date and time for display
const formatSlateDateTime = (startTime: string): { date: string; time: string } => {
  const dateObj = new Date(startTime);

  // Format date as DD/MM/YY
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear().toString().slice(-2);
  const date = `${month}/${day}/${year}`;

  // Format time
  const hours = dateObj.getHours();
  const minutes = dateObj.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  const minuteStr = minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : '';
  const time = `${hour12}${minuteStr} ${ampm} ET`;

  return { date, time };
};

const ToolsHome: React.FC<ToolsHomeProps> = ({
  subscribedLeagues,
  isAllAccess = false,
  leagueSlateInfo = {},
}) => {
  const handleUnlock = (league: string) => {
    // Navigate to pricing with the specific sport
    window.location.href = `https://stokastic.com/pricing?sport=${league.toLowerCase()}`;
  };

  // Check if a league is in season (has slates in API response)
  const isInSeason = (leagueId: string): boolean => {
    return !!leagueSlateInfo[leagueId]?.nextSlate;
  };

  // Sort leagues: active first, then locked, then offseason
  const sortedLeagues = [...ALL_LEAGUES].sort((a, b) => {
    const aInSeason = isInSeason(a.id);
    const bInSeason = isInSeason(b.id);
    const aIsActive = subscribedLeagues.includes(a.id) && aInSeason;
    const bIsActive = subscribedLeagues.includes(b.id) && bInSeason;

    if (aIsActive && !bIsActive) return -1;
    if (!aIsActive && bIsActive) return 1;
    if (!aInSeason && bInSeason) return 1;
    if (aInSeason && !bInSeason) return -1;
    return 0;
  });

  // Separate in-season and offseason leagues based on API response
  const inSeasonLeagues = sortedLeagues.filter(l => isInSeason(l.id));
  const offseasonLeagues = sortedLeagues.filter(l => !isInSeason(l.id));

  const getLeagueStatus = (league: LeagueConfig): LeagueCardProps['status'] => {
    if (!isInSeason(league.id)) return 'offseason';
    if (subscribedLeagues.includes(league.id) || isAllAccess) return 'active';
    return 'locked';
  };

  const getStatusLabel = (league: LeagueConfig): string => {
    if (!isInSeason(league.id)) return 'Offseason';

    const slateInfo = leagueSlateInfo[league.id];
    if (!slateInfo?.nextSlate) return 'Upcoming';

    return slateInfo.isLive ? 'Live' : 'Upcoming';
  };

  const getStatusTime = (league: LeagueConfig): string | undefined => {
    if (!isInSeason(league.id)) return undefined;

    const slateInfo = leagueSlateInfo[league.id];
    if (!slateInfo?.nextSlate) return undefined;

    const { date, time } = formatSlateDateTime(slateInfo.nextSlate.startTime);
    const gameCount = slateInfo.gameCount;
    const gameText = gameCount === 1 ? 'game' : 'games';

    // For sports without matchupInfo (like MMA), show just the date and time
    if (gameCount === 0) {
      return `${date} - Lock ${time}`;
    }

    return `${date} - Lock ${time} - ${gameCount} ${gameText}`;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#0A0A0A',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ToolsHeader />

      <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
        {/* Page Title */}
        <Typography
          variant="h1"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: { xs: 24, md: 32 },
            color: 'white',
            mb: 1.5,
          }}
        >
          Build DFS Lineups with Full Contest Simulations
        </Typography>

        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: { xs: 14, md: 16 },
            color: 'rgba(255, 255, 255, 0.6)',
            mb: 4,
            maxWidth: 700,
          }}
        >
          Generate lineups, simulate contests, evaluate ROI, and adjust builds using league-specific models and projections.
        </Typography>

        {/* All Access Upsell Banner - only show if not All Access and has some subscriptions */}
        {!isAllAccess && subscribedLeagues.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <AllAccessUpsellBanner subscribedLeagues={subscribedLeagues} />
          </Box>
        )}

        {/* League Selection Instruction */}
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: 14,
            color: 'rgba(255, 255, 255, 0.6)',
            mb: 3,
          }}
        >
          Select a league to start building contest sims.
        </Typography>

        {/* In-Season League Cards Grid */}
        <Grid container spacing={3} alignItems="stretch" sx={{ mb: 4 }}>
          {inSeasonLeagues.map((league) => (
            <Grid item xs={12} sm={6} md={4} key={league.id} sx={{ display: 'flex' }}>
              <LeagueCard
                league={league.name}
                leagueIcon={league.icon || undefined}
                status={getLeagueStatus(league)}
                statusLabel={getStatusLabel(league)}
                statusTime={getStatusTime(league)}
                tools={league.tools}
                onUnlock={() => handleUnlock(league.id)}
              />
            </Grid>
          ))}
        </Grid>

        {/* Offseason League Cards Grid */}
        {offseasonLeagues.length > 0 && (
          <Grid container spacing={3} alignItems="stretch" justifyContent="center" sx={{ mb: 4 }}>
            {offseasonLeagues.map((league) => (
              <Grid item xs={12} sm={6} md={4} key={league.id} sx={{ display: 'flex' }}>
                <LeagueCard
                  league={league.name}
                  leagueIcon={league.icon || undefined}
                  status="offseason"
                  tools={league.tools}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Help Footer */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={0.5}
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '8px',
            py: 2,
            px: 3,
          }}
        >
          <InfoOutlinedIcon
            sx={{
              fontSize: 18,
              color: 'rgba(255, 255, 255, 0.5)',
            }}
          />
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: 14,
              color: 'rgba(255, 255, 255, 0.6)',
            }}
          >
            Need help getting started with DFS Sims? Checkout our{' '}
            <Link
              href="https://www.youtube.com/channel/UCBUUOH0LmDuWii7zBV7SKrQ"
              target="_blank"
              sx={{
                color: '#4DA3FF',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Youtube
            </Link>{' '}
            for the best tutorials.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
};

export default ToolsHome;
